// ══════════════════════════════════════════════════════════════════
//  Queries de Time (times / time_members)
// ══════════════════════════════════════════════════════════════════
//  useTeamMemberIds: IDs dos membros ativos do time do usuário logado.
//  Base da regra de visibilidade Enterprise "leads do meu time" (ver
//  @config/enterpriseVisibility). Só busca no workspace Enterprise e quando
//  o usuário tem um time — fora disso devolve um Set vazio sem ir ao banco.
// ══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { listTeamMemberIds } from '@app';
import { isEnterpriseWorkspace } from '@config/enterprise';
import { qk } from './keys';

interface UseTeamMemberIdsResult {
  // Sempre inclui o próprio usuário (defesa contra time_members incompleto).
  teamMemberIds: Set<string>;
  isLoading: boolean;
  enabled: boolean;
}

export const useTeamMemberIds = (): UseTeamMemberIdsResult => {
  const { workspace, currentTeam, user } = useAuth();
  const enterprise = isEnterpriseWorkspace(workspace);
  const timeId = currentTeam?.id || null;
  const enabled = enterprise && !!timeId;

  const query = useQuery({
    queryKey: qk.teamMemberIds(timeId || ''),
    queryFn: () => listTeamMemberIds(timeId || ''),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const teamMemberIds = useMemo(() => {
    const set = new Set<string>(query.data || []);
    if (user?.id) set.add(user.id);
    return set;
  }, [query.data, user?.id]);

  return {
    teamMemberIds,
    isLoading: enabled && query.isLoading,
    enabled,
  };
};
